import { SettingsManager } from "@viamap/viamap2-common";

export const SERVITUT_PDF_NAMING_SETTING_KEY = "mapit.servitutPdfNamingMode";
const LEGACY_SERVITUT_PDF_NAMING_SETTING_KEY =
  "propertyInfo.servitutPdfNamingMode";

export type ServitutPdfNamingMode =
  | "standard"
  | "yearBased"
  | "priority"
  | "priorityWithHeading";

export type ServitutDownloadNameInput = {
  datoloebenr?: string;
  priorityNumber?: string | number;
  headingText?: string;
  fallbackStem?: string;
};

type DateOrder = "ddmmyyyy" | "yyyymmdd";

const DEFAULT_SERVITUT_PDF_NAMING_MODE: ServitutPdfNamingMode = "standard";

function sanitizeDownloadStem(label: string, preserveSpaces = false): string {
  const withoutIllegalChars = Array.from(label.trim(), (char) => {
    if (char.charCodeAt(0) < 32) {
      return " ";
    }
    return /[<>:"/\\|?*]/.test(char) ? " " : char;
  }).join("");
  return withoutIllegalChars
    .replace(/\s+/g, preserveSpaces ? " " : "_")
    .trim();
}

function splitFileExtension(fileName?: string): {
  baseName: string;
  extension?: string;
} {
  const trimmed = (fileName || "").trim();
  const match = /^(.*?)(?:\.([a-z0-9]{1,10}))?$/i.exec(trimmed);
  return {
    baseName: (match?.[1] || trimmed).trim(),
    extension: match?.[2]?.toLowerCase(),
  };
}

export function buildDownloadFileName(
  label: string | undefined,
  options: {
    defaultStem: string;
    defaultExtension: string;
    preserveSpaces?: boolean;
  },
): string {
  const { baseName, extension } = splitFileExtension(label);
  const sanitizedBase = sanitizeDownloadStem(
    baseName || label || options.defaultStem,
    options.preserveSpaces,
  );
  return `${sanitizedBase || options.defaultStem}.${extension || options.defaultExtension}`;
}

function getStoredNamingModeValue(): unknown {
  const nextValue = SettingsManager.getSystemSetting(
    SERVITUT_PDF_NAMING_SETTING_KEY,
    undefined,
    true,
  );
  if (nextValue !== undefined) {
    return nextValue;
  }
  return SettingsManager.getSystemSetting(
    LEGACY_SERVITUT_PDF_NAMING_SETTING_KEY,
    DEFAULT_SERVITUT_PDF_NAMING_MODE,
    true,
  );
}

function getServitutPdfNamingMode(): ServitutPdfNamingMode {
  switch (getStoredNamingModeValue()) {
    case "yearBased":
    case "priority":
    case "priorityWithHeading":
    case "standard":
      return getStoredNamingModeValue() as ServitutPdfNamingMode;
    default:
      return DEFAULT_SERVITUT_PDF_NAMING_MODE;
  }
}

function getServitutDateOrder(mode = getServitutPdfNamingMode()): DateOrder {
  return mode === "yearBased" ? "yyyymmdd" : "ddmmyyyy";
}

function parseDatoloebenr(datoloebenr?: string): {
  day: string;
  month: string;
  year: string;
  serial: string;
} | null {
  const trimmed = datoloebenr?.trim() || "";
  const match = /^(\d{2})(\d{2})(\d{4})-(.+)$/.exec(trimmed);
  if (!match) {
    return null;
  }
  return {
    day: match[1],
    month: match[2],
    year: match[3],
    serial: match[4].trim(),
  };
}

function formatDatoloebenrWithDateOrder(
  datoloebenr: string | undefined,
  dateOrder: DateOrder,
  fallbackStem?: string,
): string {
  const parsed = parseDatoloebenr(datoloebenr);
  if (!parsed) {
    return (datoloebenr || fallbackStem || "servitut").trim();
  }
  const datePart =
    dateOrder === "yyyymmdd"
      ? `${parsed.year}${parsed.month}${parsed.day}`
      : `${parsed.day}${parsed.month}${parsed.year}`;
  return `${datePart}-${parsed.serial}`;
}

function getFormattedToday(dateOrder: DateOrder): string {
  const now = new Date();
  const day = `${now.getDate()}`.padStart(2, "0");
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const year = `${now.getFullYear()}`;
  return dateOrder === "yyyymmdd"
    ? `${year}${month}${day}`
    : `${day}${month}${year}`;
}

function formatPriorityPrefix(priorityNumber?: string | number): string {
  const priorityValue = `${priorityNumber ?? ""}`.trim();
  if (/^\d$/.test(priorityValue)) {
    return priorityValue.padStart(2, "0");
  }
  return priorityValue;
}

export function getServitutDownloadFileName(
  input: ServitutDownloadNameInput,
): string {
  const mode = getServitutPdfNamingMode();
  const dateAndSerial = formatDatoloebenrWithDateOrder(
    input.datoloebenr,
    getServitutDateOrder(mode),
    input.fallbackStem,
  );
  const priorityValue = `${input.priorityNumber ?? ""}`.trim();
  const priorityPrefix = formatPriorityPrefix(input.priorityNumber);
  const headingText = (input.headingText || "").trim();

  let stem = dateAndSerial;
  if (mode === "priority") {
    stem = [priorityPrefix ? `${priorityPrefix}.` : "", dateAndSerial]
      .filter(Boolean)
      .join(" ");
  } else if (mode === "priorityWithHeading") {
    stem = [
      priorityValue ? `nr.${priorityValue}` : "",
      dateAndSerial,
      headingText,
    ]
      .filter(Boolean)
      .join(" ");
  }

  return buildDownloadFileName(stem, {
    defaultStem: "servitut",
    defaultExtension: "pdf",
    preserveSpaces: true,
  });
}

export function getServimapDownloadFileName(
  mainDocumentFileName?: string,
): string {
  return appendSuffixToPdfFileName(mainDocumentFileName, " ServiMap");
}

export function getServitutBilagDownloadFileName(
  mainDocumentFileName: string | undefined,
  bilagNumber: number,
  originalFileName?: string,
): string {
  const { baseName: rawMainBase } = splitFileExtension(mainDocumentFileName);
  const { extension } = splitFileExtension(originalFileName);
  const mainBase = sanitizeDownloadStem(rawMainBase || "servitut", true);
  return buildDownloadFileName(`${mainBase} BILAG ${bilagNumber}`, {
    defaultStem: "servitut bilag",
    defaultExtension: extension || "pdf",
    preserveSpaces: true,
  });
}

export function getTingbogAttestDownloadFileName(bfe?: string): string {
  const normalizedBfe = sanitizeDownloadStem(`${bfe || ""}`, false);
  return buildDownloadFileName(
    `${getFormattedToday(getServitutDateOrder())}_${normalizedBfe || "bfe"}_tingbogsattest`,
    {
      defaultStem: "tingbogsattest",
      defaultExtension: "pdf",
    },
  );
}

export function appendSuffixToPdfFileName(
  fileName: string | undefined,
  suffix: string,
): string {
  const { baseName } = splitFileExtension(fileName);
  return buildDownloadFileName(`${baseName || "document"}${suffix}`, {
    defaultStem: "document",
    defaultExtension: "pdf",
    preserveSpaces: true,
  });
}
